import { EventTypes, useEditor } from '@candulabs/craft';
import React from 'react';
import { useSelector } from 'react-redux';

import { useSize } from 'craft/components/Size';
import { BuiltInComponents } from 'craft/constants';
import { useMultiNodes, useResponsiveCss, useSelection } from 'craft/hooks';
import { useResolver } from 'craft/hooks/useResolver';
import { selectCollectionItemEvents } from 'craft/state';
import { CssSelectorId } from 'craft/types';
import { getAllDomNodesFromDocument } from 'craft/utils/viewport';

import { ResizeHandlers } from './ResizeHandlers';

const ResizeManager = ({
  nodeIds,
  selectedCollectionItemId,
  cssSelectorId,
  allowPercentages,
}: {
  nodeIds: string[];
  selectedCollectionItemId?: string;
  cssSelectorId?: CssSelectorId;
  allowPercentages?: boolean;
}) => {
  const { size, setSize } = useSize(nodeIds, cssSelectorId);
  const { responsiveCss } = useResponsiveCss(nodeIds, undefined, cssSelectorId);

  // this used to be in state, but was causing double renders with previously selected node and messing up the timing
  const doms = nodeIds
    .map((nodeId) => getAllDomNodesFromDocument(nodeId, selectedCollectionItemId))
    .flat()
    .filter((dom) => dom !== null && dom !== undefined) as HTMLElement[];

  // disable resizing when elements have flex-basis set
  // NOTE! because we take this from `useMultiNodes`, some nodes might still have flexBasis set.
  if (responsiveCss?.flexBasis || doms.length === 0) {
    return null;
  }

  return (
    <ResizeHandlers
      doms={doms}
      size={size}
      setSize={setSize}
      nodeIds={nodeIds}
      allowPercentages={allowPercentages}
      cssSelectorId={cssSelectorId}
    />
  );
};

export const Resize = React.memo(() => {
  const { selection } = useSelection();
  const { query } = useEditor();

  const focusedElements = query.getEvent(EventTypes.focused).all();

  const { type } = useMultiNodes(selection.all);
  const resolver = useResolver(type);
  const selectedCollectionItemId = useSelector(selectCollectionItemEvents)[EventTypes.selected];

  if (
    focusedElements.length ||
    selection.isEmpty ||
    selection.hasRootNode ||
    type === BuiltInComponents.Multiple ||
    resolver?.resize?.shouldDisableHandlers === true ||
    query.getEvent(EventTypes.dragged).size() !== 0
  ) {
    return null;
  }
  return (
    <ResizeManager
      nodeIds={selection.all}
      selectedCollectionItemId={selectedCollectionItemId}
      cssSelectorId={resolver?.toolbox?.schema?.Size?.cssSelectorId}
      allowPercentages={resolver?.toolbox?.schema?.Size?.hasPercentageSize}
    />
  );
});

Resize.displayName = 'Resize';
